// app/javascript/controllers/gallery_swiper_controller.js

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["track"]

  static values = {
    speed: { type: Number, default: 0.2 },
    direction: { type: Number, default: -1 },
    pauseInterval: { type: Number, default: 10000 },
    pauseDuration: { type: Number, default: 2000 },
  }

  connect() {
    this.x = 0

    this.isDragging = false
    this.isHovering = false
    this.isPausing = false

    // drag中の縦スクロール制御
    this.startX = 0
    this.startY = 0
    this.isHorizontal = false
    this.element.addEventListener("touchstart", (e) => {
      this.startX = e.touches[0].clientX
      this.startY = e.touches[0].clientY
      this.isHorizontal = false
    }, { passive: true })

    window.addEventListener("touchmove", (e) => {
      if (!this.isDragging) return
      const x = e.touches[0].clientX
      const y = e.touches[0].clientY
      const dx = Math.abs(x - this.startX)
      const dy = Math.abs(y - this.startY)
      if (dx > dy) {
	this.isHorizontal = true
      }
      if (this.isHorizontal) {
	e.preventDefault()
      }
    }, { passive: false })

    // 状態
    this.isDragging = false
    this.isHovering = false
    this.isPausing = false

    // ドラッグ用
    this.lastX = 0
    this.velocity = 0

    // pause用
    this.lastPauseAt = Date.now()
    this.pauseStartedAt = null

    // hover（PCのみ）
    if (window.matchMedia("(hover: hover)").matches) {
      this.onMouseEnter = () => {
        this.isHovering = true
      }

      this.onMouseLeave = () => {
        this.isHovering = false
      }

      this.element.addEventListener("mouseenter", this.onMouseEnter)
      this.element.addEventListener("mouseleave", this.onMouseLeave)
    }

    // ネイティブdrag無効
    this.onDragStart = (e) => {
      e.preventDefault()
    }
    this.element.addEventListener("dragstart", this.onDragStart)

    // PC drag
    this.onMouseDown = (e) => {
      e.preventDefault()
      this.isDragging = true
      this.lastX = e.clientX
      this.velocity = 0
    }

    this.onMouseMove = (e) => {
      if (!this.isDragging) return

      const dx = e.clientX - this.lastX
      this.x += dx
      this.velocity = dx * 0.8
      this.lastX = e.clientX
    }

    this.onMouseUp = () => {
      if (!this.isDragging) return
      this.isDragging = false
    }

    this.element.addEventListener("mousedown", this.onMouseDown)
    window.addEventListener("mousemove", this.onMouseMove)
    window.addEventListener("mouseup", this.onMouseUp)

    // SP drag
    this.onTouchStart = (e) => {
      this.isDragging = true
      this.lastX = e.touches[0].clientX
      this.velocity = 0
    }

    this.onTouchMove = (e) => {
      if (!this.isDragging) return

      const x = e.touches[0].clientX
      const dx = x - this.lastX

      this.x += dx
      this.velocity = dx * 0.8
      this.lastX = x
    }

    this.onTouchEnd = () => {
      this.isDragging = false
    }

    this.element.addEventListener("touchstart", this.onTouchStart, { passive: true })
    window.addEventListener("touchmove", this.onTouchMove, { passive: true })
    window.addEventListener("touchend", this.onTouchEnd)

    this.animate = this.animate.bind(this)
    this.raf = requestAnimationFrame(this.animate)
  }

  disconnect() {
    cancelAnimationFrame(this.raf)

    if (this.onMouseEnter) {
      this.element.removeEventListener("mouseenter", this.onMouseEnter)
      this.element.removeEventListener("mouseleave", this.onMouseLeave)
    }

    this.element.removeEventListener("dragstart", this.onDragStart)

    this.element.removeEventListener("mousedown", this.onMouseDown)
    window.removeEventListener("mousemove", this.onMouseMove)
    window.removeEventListener("mouseup", this.onMouseUp)

    this.element.removeEventListener("touchstart", this.onTouchStart)
    window.removeEventListener("touchmove", this.onTouchMove)
    window.removeEventListener("touchend", this.onTouchEnd)
  }

  animate() {
    const now = Date.now()
    let autoMove = 0

    // ----------------------------
    // pause開始判定
    // hover中 / drag中 / 慣性中 は pause開始しない
    // ----------------------------
    if (
      !this.isPausing &&
      !this.isHovering &&
      !this.isDragging &&
      Math.abs(this.velocity) < 0.1 &&
      now - this.lastPauseAt >= this.pauseIntervalValue
    ) {
      this.isPausing = true
      this.pauseStartedAt = now
    }

    // ----------------------------
    // 自動スクロール速度決定
    // 通常 → speed
    // pause中 → 減速 → 停止 → 再加速
    // hover中 → 完全停止
    // ----------------------------
    if (this.isHovering) {
      autoMove = 0

    } else if (this.isPausing) {
      const elapsed = now - this.pauseStartedAt

      const slowTime = 700
      const stopTime = this.pauseDurationValue
      const totalTime = slowTime + stopTime + slowTime

      if (elapsed < slowTime) {
        // 減速フェーズ
        const t = elapsed / slowTime
        autoMove = this.speedValue * this.directionValue * (1 - t)

      } else if (elapsed < slowTime + stopTime) {
        // 停止フェーズ
        autoMove = 0

      } else if (elapsed < totalTime) {
        // 再加速フェーズ
        const t = (elapsed - slowTime - stopTime) / slowTime
        autoMove = this.speedValue * this.directionValue * t

      } else {
        // pause終了
        this.isPausing = false
        this.pauseStartedAt = null
        this.lastPauseAt = now
        autoMove = this.speedValue * this.directionValue
      }

    } else {
      autoMove = this.speedValue * this.directionValue
    }

    // ----------------------------
    // 移動量決定
    // drag中はmousemove/touchmoveで直接 x を変えてるので何もしない
    // drag後は慣性優先
    // ----------------------------
    let move = 0

    if (this.isDragging) {
      move = 0

    } else if (Math.abs(this.velocity) > 0.1) {
      move = this.velocity + autoMove
      this.velocity *= 0.95

    } else {
      move = autoMove
      this.velocity = 0
    }

    this.x += move

    // ----------------------------
    // 無限ループ
    // 右投げにも左投げにも対応
    // ----------------------------
    const width = this.trackTarget.scrollWidth / 2

    while (this.x <= -width) {
      this.x += width
    }

    while (this.x >= 0) {
      this.x -= width
    }

    this.trackTarget.style.transform = `translate3d(${this.x}px, 0, 0)`

    this.raf = requestAnimationFrame(this.animate)
  }
}
